import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { ArrowLeft, Edit2, Plus, Search } from "lucide-react";

import { API_PROJECT_WORKSTREAM_TRACKING } from "../../../config/Endpoints.js";
import { invalidateProjectDesignationOptions } from "../../../Hooks/useProjectDesignationOptions.js";
import { Card } from "../../components/shared/card/card.tsx";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Button, Modal, Select, Spinner, Table, TableBody, TableCell, TableHeadCell, TableHeader, TablePagination, TableRow, TextField } from "../../components/ui/index.ts";
import { useBreadcrumbs } from "../../context/breadcrumb/breadcrumb-context.tsx";
import { useUser } from "../../lib/rbac/useUser.ts";
import { getApi, postApi, putApi } from "../../utils/api.ts";
import { showError, showSuccess } from "../../utils/toast.ts";
import styles from "./workstream-configuration-page.module.css";

type GlobalRate = {
  id: number | null;
  designationCode: string;
  active: boolean;
  designationName: string;
  locationType: "ONSHORE" | "OFFSHORE";
  currency: "USD";
  commercialRate: number | null;
  internalRate: number | null;
};

const endpoint = `${API_PROJECT_WORKSTREAM_TRACKING}/global-designations`;
const DEFAULT_PAGE_SIZE = 10;
const formatRate = (value: number | null) => value == null ? "Not configured" : `$${Number(value).toFixed(2)}`;

export default function GlobalDesignationPage() {
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumbs();
  const user = useUser();
  const canManage = user != null && (Number(user.id) === 1 || Number(user.id) === 271);
  const [rates, setRates] = useState<GlobalRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [designationName, setDesignationName] = useState("");
  const [locationType, setLocationType] = useState("");
  const [internalRate, setInternalRate] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [dialogMode, setDialogMode] = useState<"create" | "edit" | null>(null);
  const [selectedRateId, setSelectedRateId] = useState<number | null>(null);
  const token = Cookies.get("accessToken");

  useEffect(() => {
    setBreadcrumbs([
      { label: "Project Configuration", path: "/dashboard/configuration" },
      { label: "Global Designations" },
    ]);
    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs]);

  useEffect(() => {
    if (!canManage) { setLoading(false); return; }
    const controller = new AbortController();
    setLoading(true);
    setLoadError(false);
    void getApi<GlobalRate[]>(endpoint, token, "json", controller.signal)
      .then((response) => { if (!controller.signal.aborted) setRates(response.data); })
      .catch((error) => {
        if (!controller.signal.aborted && error?.name !== "AbortError") setLoadError(true);
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [token, canManage]);

  const visibleRates = useMemo(() => rates.filter((rate) =>
    `${rate.designationName} ${rate.locationType}`.toLowerCase().includes(query.toLowerCase().trim()))
    .sort((left, right) => left.designationName.localeCompare(right.designationName) || left.locationType.localeCompare(right.locationType)), [rates, query]);
  const totalPages = Math.max(1, Math.ceil(visibleRates.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRates = visibleRates.slice((safePage - 1) * pageSize, safePage * pageSize);

  const openCreate = () => {
    setSelectedRateId(null);
    setDesignationName("");
    setLocationType("");
    setInternalRate("");
    setDialogMode("create");
  };

  const selectRate = (rate: GlobalRate) => {
    setSelectedRateId(rate.id);
    setDesignationName(rate.designationName);
    setLocationType(rate.locationType);
    setInternalRate(rate.internalRate == null ? "" : String(rate.internalRate));
    setDialogMode("edit");
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!designationName || !locationType || saving) return;
    const normalizedName = designationName.trim().replace(/\s+/g, " ");
    if (rates.some((rate) => rate.id !== selectedRateId && rate.locationType === locationType &&
        rate.designationName.trim().replace(/\s+/g, " ").toLocaleLowerCase() === normalizedName.toLocaleLowerCase())) {
      showError("This designation and type already exist");
      return;
    }
    if (dialogMode === "edit" && selectedRateId == null) {
      showError("Unable to identify this designation");
      return;
    }
    const internal = internalRate.trim() === "" ? null : Number(internalRate);
    if (internal != null && (!Number.isFinite(internal) || internal < 0)) {
      showError("Internal rate must be a non-negative number");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        designationName: normalizedName, locationType, internalRate: internal,
      };
      const response = dialogMode === "create"
        ? await postApi<GlobalRate>(endpoint, token, payload)
        : await putApi<GlobalRate>(`${endpoint}/${selectedRateId}`, token, payload);
      const updated = response.data;
      setRates((current) => dialogMode === "create" ? [...current, updated] : current.map((rate) =>
        rate.id === updated.id ? updated : rate));
      invalidateProjectDesignationOptions();
      setDialogMode(null);
      showSuccess(dialogMode === "create" ? "Designation created" : "Designation rates updated");
    } catch (error) {
      showError(error instanceof Error ? error.message : "Unable to save designation");
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) return <PageShell><div className={styles.empty}>Only authorized users can manage global designation rates.</div></PageShell>;

  return <PageShell>
    <div className={styles.projectListView}>
      <Card padding="none" className={styles.listCard}>
        <div className={styles.globalToolbar}>
          <label><Search size={17} /><input aria-label="Search designations" placeholder="Search designation or type" value={query} onChange={(event) => { setQuery(event.target.value); setPage(1); }} /></label>
          <div className={styles.globalToolbarActions}>
            <Button startIcon={<Plus size={16} />} onClick={openCreate}>Create Designation</Button>
            <Button variant="light-grey" startIcon={<ArrowLeft size={16} />} onClick={() => navigate("/dashboard/configuration")}>Back</Button>
          </div>
        </div>
        {loading ? <div className={styles.empty}><Spinner variant="inline" /> Loading designations...</div>
          : loadError ? <div className={`${styles.empty} ${styles.error}`}>Unable to load global designations. Check that the master table has been created.</div>
          : <><Table>
            <TableHeader><TableRow><TableHeadCell>Designation</TableHeadCell><TableHeadCell>Type</TableHeadCell><TableHeadCell>Internal rate</TableHeadCell><TableHeadCell>Action</TableHeadCell></TableRow></TableHeader>
            <TableBody>{pageRates.map((rate) => <TableRow key={`${rate.designationName}-${rate.locationType}`}>
              <TableCell>{rate.designationName}</TableCell><TableCell>{rate.locationType === "ONSHORE" ? "Onshore" : "Offshore"}</TableCell>
              <TableCell>{formatRate(rate.internalRate)}</TableCell>
              <TableCell><Button size="sm" variant="accent-ghost" startIcon={<Edit2 size={14} />} onClick={() => selectRate(rate)}>Configure</Button></TableCell>
            </TableRow>)}{pageRates.length === 0 && <TableRow><TableCell colSpan={4}>No designations match your search.</TableCell></TableRow>}</TableBody>
          </Table>
          <TablePagination className={styles.globalPagination} page={safePage} pageSize={pageSize} totalItems={visibleRates.length} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} pageSizeOptions={[10, 20, 50]} /></>}
      </Card>
    </div>
    <Modal open={dialogMode != null} onClose={() => { if (!saving) setDialogMode(null); }} title={dialogMode === "create" ? "Create Designation" : "Configure Designation"}
      footer={<div className={styles.actions}><Button type="submit" form="global-designation-form" disabled={saving}>{saving ? "Saving..." : dialogMode === "create" ? "Create Designation" : "Save Changes"}</Button><Button variant="light-grey" disabled={saving} onClick={() => setDialogMode(null)}>Cancel</Button></div>}>
      <form id="global-designation-form" className={styles.modalForm} onSubmit={(event) => void submit(event)}>
        <TextField id="global-designation-name" label="Designation" required maxLength={150} value={designationName} onChange={(event) => setDesignationName(event.target.value)} placeholder="Enter designation name" />
        <Select label="Designation Type" required disabled={dialogMode === "edit"} value={locationType} onChange={setLocationType} options={[{ value: "ONSHORE", label: "Onshore" }, { value: "OFFSHORE", label: "Offshore" }]} />
        <TextField id="global-internal-rate" label="Internal rate (USD/hour)" type="number" min="0" step="0.01" value={internalRate} onChange={(event) => setInternalRate(event.target.value)} />
      </form>
    </Modal>
  </PageShell>;
}
